// Geport vanuit de desktop-app (src/utils/transcriptSpeaker.ts).
// Webtranscripten bevatten vrije sprekersnamen; namen die niet als
// "Spreker N" parsen krijgen via een stabiele hash toch een eigen kleur.

export type TranscriptSpeaker =
    | { kind: 'them' }
    | { kind: 'remote'; index: number };

type TranscriptSpeakerPayload = TranscriptSpeaker | {
    kind?: string | null;
    index?: number | null;
} | string | null | undefined;

export type TranscriptSpeakerPalette = {
    accent: string;
    surface: string;
    ink: string;
};

const REMOTE_PALETTE: TranscriptSpeakerPalette[] = [
    { accent: '#9a3412', surface: 'rgba(154, 52, 18, 0.12)', ink: '#7c2d12' },
    { accent: '#0f766e', surface: 'rgba(15, 118, 110, 0.12)', ink: '#115e59' },
    { accent: '#1d4ed8', surface: 'rgba(29, 78, 216, 0.12)', ink: '#1e40af' },
    { accent: '#7c3aed', surface: 'rgba(124, 58, 237, 0.12)', ink: '#6d28d9' },
    { accent: '#b45309', surface: 'rgba(180, 83, 9, 0.12)', ink: '#92400e' },
    { accent: '#be185d', surface: 'rgba(190, 24, 93, 0.12)', ink: '#9d174d' },
];

const THEM_PALETTE: TranscriptSpeakerPalette = {
    accent: '#4b6b73',
    surface: 'rgba(75, 107, 115, 0.12)',
    ink: '#26434a',
};

const parseRemoteIndex = (value: string) => {
    const remoteMatch = value.match(/^(?:remote|speaker|spreker)[\s:_-]*(\d+)$/i);
    if (!remoteMatch) {
        return null;
    }

    const parsed = Number(remoteMatch[1]);
    if (!Number.isFinite(parsed)) {
        return null;
    }

    return Math.max(0, parsed - 1);
};

const hashLabel = (value: string): number => {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
};

export const parseTranscriptSpeaker = (value: TranscriptSpeakerPayload): TranscriptSpeaker | null => {
    if (!value) {
        return null;
    }

    if (typeof value === 'object' && 'kind' in value) {
        if (value.kind === 'remote' && Number.isFinite(value.index)) {
            return { kind: 'remote', index: Math.max(0, Math.floor(value.index || 0)) };
        }
        if (value.kind === 'them') {
            return { kind: 'them' };
        }
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    if (!normalized) {
        return null;
    }

    const lowered = normalized.toLowerCase();
    if (
        lowered === 'them'
        || lowered === 'live'
        || lowered === 'gesprekspartner'
        || lowered === 'participant'
        || lowered === 'deelnemer'
    ) {
        return { kind: 'them' };
    }

    const remoteIndex = parseRemoteIndex(normalized);
    if (remoteIndex !== null) {
        return { kind: 'remote', index: remoteIndex };
    }

    return null;
};

export const getTranscriptSpeakerPalette = (
    value: TranscriptSpeakerPayload,
    fallbackLabel?: string | null,
): TranscriptSpeakerPalette => {
    const parsed = parseTranscriptSpeaker(value);

    if (parsed?.kind === 'them') {
        return THEM_PALETTE;
    }

    if (parsed?.kind === 'remote') {
        return REMOTE_PALETTE[parsed.index % REMOTE_PALETTE.length];
    }

    const label = (typeof value === 'string' ? value : fallbackLabel ?? '').trim();
    if (!label) {
        return THEM_PALETTE;
    }

    return REMOTE_PALETTE[hashLabel(label.toLowerCase()) % REMOTE_PALETTE.length];
};
